import React, { useEffect, useMemo } from 'react'
import queryString, { ParsedQuery } from 'query-string'
import { useLocation, useNavigate } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'
import {
  UserRecord,
  UserState,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { PERMISSIONS } from 'src/config/roleConfig'
import { useFilterLabels } from '../FilterLabelsContext'
import { useUsers } from '../../../../async/Users'
import { FilterName, FilterParam } from '../FilterBar.types'
import { Spinner } from '../../../../../Throbber'
import { CountChip } from '../../../Chips/BasicChip'
import { CheckBoxInput } from '../../../Inputs/CheckBox'
import { MenuPlaceholder } from '../../../../Placeholders/MenuPlaceholder'
import { FilterDropdownItem, FilterDropdownToggle } from '../FilterBar.styles'
import { Assignee } from '../../../../../Utils/globalEnums'
import { DropDownMenu } from './FilterDropdown.styles'

const OwnerItem = ({
  name,
  email,
  id,
  searchParams,
  appliedFilters,
}: {
  name?: string
  email: string
  id: string
  searchParams: ParsedQuery
  appliedFilters: string[]
}): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const isRequestsPage = location.pathname.includes('requests')
  const userArg = isRequestsPage ? id : `${email?.toLowerCase()}`
  const newAppliedFilters = new Set(appliedFilters.map((f) => f.toLowerCase()))
  const isChecked = newAppliedFilters.has(userArg)
  isChecked ? newAppliedFilters.delete(userArg) : newAppliedFilters.add(userArg)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...searchParams,
        [FilterParam.OWNER]: Array.from(newAppliedFilters),
      },
    },
    { arrayFormat: 'bracket' },
  )

  return (
    <FilterDropdownItem replace to={href}>
      <CheckBoxInput
        id={email}
        label={name || email}
        checked={isChecked}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onChange={() => {
          navigate(href, { replace: true })
        }}
        tabIndex={-1}
        requiredPermissions={[PERMISSIONS.READ]}
      />
    </FilterDropdownItem>
  )
}

export const OwnerFilterDropdown = React.memo(
  ({
    customName,
    includeUnassigned = true,
    isNested = false,
  }: {
    customName?: string
    includeUnassigned?: boolean
    isNested?: boolean
  }): JSX.Element => {
    const location = useLocation()
    const userResp = useUsers()
    const { registerFilter } = useFilterLabels()

    useEffect(() => {
      const users = userResp.data?.getUsersList() ?? []
      if (users.length === 0) return
      const valueMap: Record<string, string> = {}
      users.forEach((u) => {
        const display = u.getName() || u.getEmail()
        valueMap[u.getEmail()] = display
        valueMap[u.getId()] = display
      })
      registerFilter(FilterParam.OWNER, FilterName.OWNER, valueMap)
    }, [userResp.data, registerFilter])

    const searchParams = useMemo(() => {
      return queryString.parse(location.search, {
        arrayFormat: 'bracket',
      })
    }, [location])

    const appliedFilters = useMemo(() => {
      const preprocessed = searchParams[FilterParam.OWNER]
      return Array.isArray(preprocessed)
        ? preprocessed
        : preprocessed
        ? [preprocessed]
        : []
    }, [searchParams])

    if (userResp.error) return <></>
    if (!userResp.data || userResp.isLoading) {
      return (
        <>
          <Dropdown.Toggle
            as={FilterDropdownToggle}
            id={`${FilterName.OWNER}-filter-dropdown`}
            $isNested={isNested}
          >
            <p>{customName || FilterName.OWNER}</p>
            <Spinner size="s" color="primary" mx="xxs" />
          </Dropdown.Toggle>
          <DropDownMenu>
            <MenuPlaceholder height={108} />
          </DropDownMenu>
        </>
      )
    }
    const users = userResp.data
      .getUsersList()
      .filter((user) => user.getState() === UserState.USER_ACTIVE)

    return (
      <Dropdown autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          id={`${FilterName.OWNER}-filter-dropdown`}
          $hasSelection={appliedFilters.length > 0}
          $isNested={isNested}
        >
          <p>{customName || FilterName.OWNER}</p>
          {appliedFilters.length > 0 && (
            <CountChip ml="xxs">{appliedFilters.length}</CountChip>
          )}
        </Dropdown.Toggle>
        <DropDownMenu>
          {includeUnassigned && (
            <OwnerItem
              key={Assignee.UNASSIGNED}
              name={Assignee.UNASSIGNED}
              email={Assignee.UNASSIGNED_BE}
              id={Assignee.UNASSIGNED_BE}
              searchParams={searchParams}
              appliedFilters={appliedFilters}
            />
          )}
          {users.map((user: UserRecord) => {
            return (
              <OwnerItem
                key={user.getEmail()}
                name={user.getName()}
                email={user.getEmail()}
                id={user.getId()}
                searchParams={searchParams}
                appliedFilters={appliedFilters}
              />
            )
          })}
        </DropDownMenu>
      </Dropdown>
    )
  },
)
